// Single entry — Vite extracts the SCSS into a <link> in <head>.
import '../scss/master.scss';

import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { SplitText } from 'gsap/SplitText';
import { setupHeroWave } from './hero-wave.js';

gsap.registerPlugin(ScrollTrigger, SplitText);

const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
const supportsHover = window.matchMedia('(hover: hover)').matches;

// Lenis (Phase 2B) was removed in Phase 2E as a diagnostic — Phase 1.2
// pin behaviour worked correctly with native scroll, and the post-2B
// reports of services/.history overlap correlated with Lenis being
// in the chain. If/when smooth scroll is wanted again, re-add via
// ScrollTrigger.scrollerProxy() for proper integration.

// Noise overlay removed at user's call — markup + SCSS + this function
// + the init() call have all been stripped. Grain effect is gone site-wide.

// ---------- Hero wave (Three.js dot-grid sea surface) ---------------------
//
// Implementation lives in ./hero-wave.js so the WebGL + shader code stays
// out of master.js's flow. The function returns a {dispose} handle that
// the HMR cleanup at the bottom of this file calls on hot-reload, so
// each dev save replaces the renderer cleanly instead of stacking
// animators on the same <canvas>.
let heroWaveHandle = null;

// ---------- Theme: follow device, remember a manual choice for 24h ---------
// The inline <head> script applies the initial theme before paint (a fresh
// saved choice if < 24h old, otherwise the OS preference). Here we sync the
// toggle UI, persist manual choices with a timestamp, and keep following the
// OS live until the visitor makes a choice.
const THEME_KEY = 'bsTheme';
const THEME_TTL = 86400000; // 24 hours in ms

function readSavedTheme() {
  try {
    const o = JSON.parse(localStorage.getItem(THEME_KEY) || 'null');
    if (o && (o.theme === 'light' || o.theme === 'dark') &&
        typeof o.ts === 'number' && Date.now() - o.ts < THEME_TTL) {
      return o.theme;
    }
    localStorage.removeItem(THEME_KEY);
  } catch (e) { /* malformed / disabled storage — fall back to device */ }
  return null;
}

function deviceTheme() {
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

function setupThemeToggle() {
  const toggle = document.querySelector('.dark-mode-toggle');

  const syncToggle = (theme) => {
    if (!toggle) return;
    toggle.classList.toggle('dark-mode', theme === 'dark');
    toggle.setAttribute('aria-checked', String(theme === 'dark'));
  };

  // <html data-theme> was already resolved + applied by the inline head
  // script before paint — trust it rather than recomputing here.
  const current = document.documentElement.getAttribute('data-theme') || deviceTheme();
  if (toggle) {
    toggle.setAttribute('role', 'switch');
    toggle.setAttribute('aria-label', 'Toggle dark mode');
    syncToggle(current);

    toggle.addEventListener('click', () => {
      const next = document.documentElement.getAttribute('data-theme') === 'dark' ? 'light' : 'dark';
      document.documentElement.setAttribute('data-theme', next);
      syncToggle(next);
      try {
        localStorage.setItem(THEME_KEY, JSON.stringify({ theme: next, ts: Date.now() }));
      } catch (e) { /* storage disabled — choice just won't persist */ }
      document.dispatchEvent(new CustomEvent('themechange', { detail: { theme: next } }));
    });
  }

  // Follow the OS live, but only while there is no fresh manual choice.
  const mq = window.matchMedia('(prefers-color-scheme: dark)');
  const onSchemeChange = () => {
    if (readSavedTheme() !== null) return;
    const theme = deviceTheme();
    document.documentElement.setAttribute('data-theme', theme);
    syncToggle(theme);
    document.dispatchEvent(new CustomEvent('themechange', { detail: { theme } }));
  };
  if (mq.addEventListener) mq.addEventListener('change', onSchemeChange);
  else if (mq.addListener) mq.addListener(onSchemeChange);
}

// ---------- Show .title in nav once user scrolls past the hero -------------
function setupTitleScroll() {
  if (window.innerWidth < 768) return;
  const titles = document.querySelectorAll('.title');
  if (!titles.length) return;
  const onScroll = () => {
    const show = window.scrollY > 767;
    titles.forEach((el) => el.classList.toggle('show-title', show));
  };
  window.addEventListener('scroll', onScroll, { passive: true });
  onScroll();
}

// ---------- Vanilla navbar collapse (replaces bootstrap.bundle.min.js) -----
function setupNavToggle() {
  const toggler = document.querySelector('.nav-toggle');
  const target = document.getElementById('navbarNav');
  if (!toggler || !target) return;

  const setOpen = (open) => {
    target.classList.toggle('show', open);
    toggler.setAttribute('aria-expanded', String(open));
    // Lock body scroll while the full-screen drawer is open (matched
    // by `body.nav-open { overflow: hidden; }` in _navbar.scss).
    document.body.classList.toggle('nav-open', open);
  };

  toggler.addEventListener('click', () => setOpen(!target.classList.contains('show')));

  // Close when a nav link is followed (mobile UX)
  target.querySelectorAll('a').forEach((a) => {
    a.addEventListener('click', () => setOpen(false));
  });

  // Close on Escape
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && target.classList.contains('show')) setOpen(false);
  });
}

// ---------- GSAP scroll-driven animations ----------------------------------
// All scroll-driven motion lives behind gsap.matchMedia(). When a query stops
// matching (e.g. user resizes 1440 → 768), GSAP runs the scoped cleanup,
// kills the ScrollTriggers, and clears any inline transforms. This is the
// canonical fix for the "horizontal timeline stuck at negative X on mobile"
// bug — the previous code re-killed triggers on resize but never reset the
// inline `transform` left behind by `gsap.to(el, { x: ... })`.
function setupAnimations() {
  // Defensive: clear any ScrollTriggers + pin-spacers left over from a
  // previous run (e.g. dev-server HMR re-running this module). Without
  // this, multiple HMR cycles can accumulate stacked pin-spacers around
  // .history, which renders as a visible "duplicate" of the section.
  ScrollTrigger.getAll().forEach((t) => t.kill(true));

  // Reduced-motion users get a single fade-up for the bio words; no scrub,
  // no parallax, no horizontal pin. Everything else is static.
  if (reducedMotion) {
    const el = document.querySelector('.split-word');
    if (el) {
      const split = new SplitText(el, { type: 'words' });
      gsap.set(split.words, { opacity: 1 });
    }
    return;
  }

  // SplitText reveal (all viewports). Word-by-word fade from 0.5 → 1
  // opacity with a color tween, scrubbed to scroll. Re-runs on theme
  // change so the words land at the correct end-color.
  let split;
  let splitTl;

  const buildSplit = () => {
    split && split.revert();
    splitTl && splitTl.kill();

    const target = document.querySelector('.split-word');
    if (!target) return;

    split = new SplitText(target, { type: 'words' });
    const colour = document.documentElement.getAttribute('data-theme') === 'dark'
      ? '#fff'
      : '#000';

    splitTl = gsap.timeline({
      scrollTrigger: {
        trigger: '.split-word',
        start: 'top 70%',
        end: 'bottom 80%',
        scrub: 1,
      },
    });

    splitTl.fromTo(
      split.words,
      // Start opacity dropped 0.5 → 0.2 at the user's call so the word-
      // by-word reveal reads more prominently — words begin much fainter
      // and brighten through a larger range as they scroll into view.
      { opacity: 0.2 },
      { opacity: 1, color: colour, stagger: 0.1, duration: 1, ease: 'power2.out' }
    );
  };

  buildSplit();
  document.addEventListener('themechange', buildSplit);

  const mm = gsap.matchMedia();

  // Hero parallax + blur — every viewport. Targets the Three.js
  // canvas (.hero-wave) since the headshot photo / ASCII experiments
  // have been replaced. CSS filter:blur on a canvas works the same as
  // on an <img>: the browser rasterises the canvas as a compositor
  // layer and applies the filter — visually identical, no perf hit at
  // hero-sized resolutions.
  mm.add('(min-width: 1px)', () => {
    gsap.to('.hero-img .hero-wave', {
      y: '-300px',
      ease: 'none',
      scrollTrigger: {
        trigger: '.hero-text',
        start: 'top top',
        end: 'bottom top',
        scrub: true,
      },
    });

    gsap.fromTo(
      '.hero-img .hero-wave',
      { filter: 'blur(0px)' },
      {
        filter: 'blur(30px)',
        opacity: 0.5,
        ease: 'power2.out',
        scrollTrigger: {
          trigger: '.hero-text',
          // Delayed start: wave stays full strength for the first 600 px
          // of scroll from the hero top, then begins to blur + fade. End
          // sits another 400 px past start so the fade resolves cleanly
          // within the hero scroll range. User asked to "keep the wave
          // in viewport longer when starting to scroll" — these values
          // are the dial-in.
          start: 'top+=600 top',
          end: 'top+=1000 top',
          scrub: true,
        },
      }
    );
  });

  // Hero aside pin — desktop only.
  // Guarded with a DOM check so this no-ops on pages where the
  // .hero-aside column has been removed (e.g. the UKVI homepage where
  // the play-reel is dropped per Priority 6). Restoring the reel
  // markup re-activates the pin automatically.
  mm.add('(min-width: 1024px)', () => {
    if (!document.querySelector('.hero-aside')) return;
    ScrollTrigger.create({
      trigger: '.hero-text',
      start: 'top top',
      end: 'bottom bottom',
      pin: '.hero-aside',
      pinSpacing: false,
    });
  });

  // Horizontal timeline — desktop only.
  // Below 1024px the SCSS handles the layout: tablet (641–1023) gets a
  // native touch-swipe with scroll-snap; phone (≤640) stacks vertically.
  // Phones never enter this branch, so there's no GSAP transform to leak.
  mm.add('(min-width: 1024px)', () => {
    const horizontalTimeline = document.querySelector('.horizontal-timeline');
    if (!horizontalTimeline) return;

    // ONE ScrollTrigger that both pins .history AND drives the timeline's
    // X transform. Two separate triggers fighting for the same pin position
    // caused GSAP to misfire on engagement (the Phase 1.0 / 1.1 "duplicate
    // history" bug — .services items 4–5 visibly overlapped the pinned
    // .history because the pin engaged before .services had fully scrolled
    // out of view).
    //
    // Pin distance = (scrollWidth - innerWidth) — the actual scroll needed
    // to reveal the off-screen tail of the timeline. The previous code used
    // `+= scrollWidth` which left an extra `innerWidth` of dead-zone scroll
    // where the pinned section sat static (the user's "massive space").
    gsap.to(horizontalTimeline, {
      x: () => -(horizontalTimeline.scrollWidth - window.innerWidth),
      ease: 'none',
      scrollTrigger: {
        trigger: '.history',
        start: 'top top',
        end: () => '+=' + (horizontalTimeline.scrollWidth - window.innerWidth),
        pin: true,
        scrub: 1,
        // Recalculate on viewport changes so layout shifts (font load, etc.)
        // don't leave the pin captured at a stale scroll position.
        invalidateOnRefresh: true,
      },
    });

    // Cleanup: when this query no longer matches (resize down to tablet/phone)
    // GSAP auto-kills the ScrollTrigger above and runs this fn — clear the
    // inline transform so the native scroll layout doesn't start at -1500px.
    return () => {
      gsap.set(horizontalTimeline, { clearProps: 'transform,x' });
    };
  });

  // Refresh ScrollTrigger after every milestone where layout could shift.
  // Without these, ScrollTrigger caches element positions BEFORE fonts /
  // images / videos have decoded, then pin triggers fire at the wrong
  // scrollY — symptom: pinned section overlaps the section above it
  // because the pin engaged 100–200 px too early.
  //
  //   1. requestAnimationFrame: immediately after init, in case layout
  //      already settled (cached fonts, instant CSS load)
  //   2. document.fonts.ready: after Manrope finishes loading and the
  //      browser reflows the headings to the final glyph metrics
  //   3. window 'load': fires after every sub-resource (images, videos,
  //      iframes) has resolved
  //   4. ResizeObserver on <body>: catches every other layout shift —
  //      Vite's CSS hot-injection in dev (CSS arrives after JS, layout
  //      jumps), late image decode, dynamic content. THIS is the one
  //      that fixes the dev-only services/history overlap: in
  //      production CSS is in <head> as blocking <link>, so layout is
  //      stable before JS runs — but Vite's dev server injects CSS via
  //      JS modules *after* setupAnimations() has already captured pin
  //      positions against the un-styled layout.
  requestAnimationFrame(() => ScrollTrigger.refresh());
  if (document.fonts && document.fonts.ready) {
    document.fonts.ready.then(() => ScrollTrigger.refresh());
  }
  window.addEventListener('load', () => ScrollTrigger.refresh());

  if (typeof ResizeObserver !== 'undefined') {
    // ScrollTrigger.refresh() recalculates pin-spacer dimensions, which
    // can change body.height — re-triggering ResizeObserver and looping
    // forever. Use lastHeight tracking + a refreshing flag to break the
    // loop, and only react to *real* layout changes (not the side-effect
    // of refresh() itself).
    let throttle;
    let lastHeight = document.body.offsetHeight;
    let refreshing = false;
    const ro = new ResizeObserver(() => {
      if (refreshing) return;
      const h = document.body.offsetHeight;
      if (h === lastHeight) return;
      lastHeight = h;
      clearTimeout(throttle);
      throttle = setTimeout(() => {
        refreshing = true;
        ScrollTrigger.refresh();
        // Update lastHeight to whatever refresh produced, so the next
        // ResizeObserver tick (caused by refresh itself) is a no-op.
        lastHeight = document.body.offsetHeight;
        refreshing = false;
      }, 150);
    });
    ro.observe(document.body);
  }
}

// ---------- Magnetic cursor on CTAs ---------------------------------------
// Subtle pull on key links / buttons — they drift toward the cursor when
// it's near, snap back on leave. Hover-capable pointers only (touch
// devices skip this entirely; it'd never trigger anyway).
//
// Selector list is intentionally narrow: only "primary" CTAs that benefit
// from the affordance. Don't sprinkle this on every link or it stops
// reading as intentional.
function setupMagneticCursors() {
  if (!supportsHover || reducedMotion) return;

  const targets = document.querySelectorAll(
    '.cta-link, .play-btn, .footer-cta, .contact-row, .resume-link'
  );

  targets.forEach((el) => {
    let raf = 0;
    const onMove = (e) => {
      const r = el.getBoundingClientRect();
      const x = e.clientX - (r.left + r.width / 2);
      const y = e.clientY - (r.top + r.height / 2);
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        gsap.to(el, {
          x: x * 0.18,
          y: y * 0.18,
          duration: 0.45,
          ease: 'power3.out',
          overwrite: 'auto',
        });
      });
    };
    const onLeave = () => {
      cancelAnimationFrame(raf);
      gsap.to(el, {
        x: 0,
        y: 0,
        duration: 0.6,
        ease: 'elastic.out(1, 0.55)',
        overwrite: 'auto',
      });
    };

    el.addEventListener('mousemove', onMove);
    el.addEventListener('mouseleave', onLeave);
  });
}

// ---------- Footer year + viewer timezone ----------------------------------
function setupFooterMeta() {
  // Always-correct copyright year — no need to remember to bump the markup.
  const yearEl = document.querySelector('[data-year]');
  if (yearEl) yearEl.textContent = String(new Date().getFullYear());

  // Show the visitor's local timezone abbreviation next to the locale string,
  // so a UK-based viewer sees "GMT" / "BST" and an LA viewer sees "PST" etc.
  const tzEl = document.querySelector('[data-tz]');
  if (tzEl) {
    try {
      const parts = new Intl.DateTimeFormat('en-GB', { timeZoneName: 'short' })
        .formatToParts(new Date());
      const tz = parts.find((p) => p.type === 'timeZoneName')?.value;
      if (tz) tzEl.textContent = tz;
    } catch {
      // Older browser — keep the markup default
    }
  }
}

// ---------- Init -----------------------------------------------------------
function init() {
  heroWaveHandle = setupHeroWave({ reducedMotion });
  setupThemeToggle();
  setupTitleScroll();
  setupNavToggle();
  setupFooterMeta();
  setupMagneticCursors();
  setupAnimations();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}

// Vite HMR cleanup — without this, every hot-replace would stack a fresh
// set of ScrollTriggers + pin-spacers on top of the existing ones, which
// renders as a visible "duplicate" of any pinned section (e.g. .history).
if (import.meta.hot) {
  import.meta.hot.dispose(() => {
    ScrollTrigger.getAll().forEach((t) => t.kill(true));
    // Remove any lingering pin-spacers (kill(true) usually does this but
    // guards against edge cases where the parent has been re-mounted).
    document.querySelectorAll('.pin-spacer').forEach((el) => {
      const child = el.firstElementChild;
      if (child) el.replaceWith(child);
    });
    // Tear down the Three.js renderer + rAF loop so each save replaces
    // the wave cleanly instead of stacking renderers on the same canvas.
    if (heroWaveHandle && typeof heroWaveHandle.dispose === 'function') {
      heroWaveHandle.dispose();
      heroWaveHandle = null;
    }
  });
}
